"use client";

import { useState } from "react";
import Image from "next/image";

import type { DemoFinetune, HeroPointOverlay } from "@/lib/demo-finetunes";
import PageContent from "@/components/page-content";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeading } from "./SectionHeading";
import { PointOverlay } from "./PointOverlay";
import { ImageLightbox } from "./ImageLightbox";

interface PointShowcaseProps {
  finetune: DemoFinetune;
}

function ShowcaseCard({
  imageSrc,
  imageAlt,
  imageWidth,
  imageHeight,
  overlay,
  label,
}: {
  imageSrc: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
  overlay: HeroPointOverlay;
  label: string;
}) {
  const lightboxContent = (
    <div className="relative h-full w-full">
      <Image
        src={imageSrc}
        alt={imageAlt}
        fill
        sizes="100vw"
        className="object-contain"
      />
      <PointOverlay overlay={overlay} />
    </div>
  );

  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <ImageLightbox lightboxContent={lightboxContent}>
        <div className="relative aspect-square overflow-hidden rounded-lg bg-black/5 dark:bg-white/5">
          <Image
            src={imageSrc}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            sizes="(min-width: 1024px) 500px, 90vw"
            className="h-full w-full object-contain"
          />
          <PointOverlay overlay={overlay} />
        </div>
      </ImageLightbox>
    </div>
  );
}

export function PointShowcase({ finetune }: PointShowcaseProps) {
  const showcase = finetune.pointShowcase;
  const [activeIndex, setActiveIndex] = useState(0);

  if (!showcase?.length) return null;

  const example = showcase[activeIndex];

  return (
    <PageContent>
      <section className="space-y-8">
        <SectionHeading
          eyebrow="Results"
          title="See it in action"
          description="Side-by-side point detection on aerial imagery. Base model on the left, fine-tuned on the right."
        />

        {/* Dots + legend */}
        <div className="flex items-center gap-2">
          {showcase.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveIndex(i)}
              aria-label={`Example ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === activeIndex
                  ? "w-6 bg-foreground"
                  : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
            />
          ))}

          <div className="ml-auto flex items-center gap-5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
              Correct
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
              False positive
            </span>
          </div>
        </div>

        {/* Active example */}
        <Card className="overflow-hidden border-border/60 bg-card/80 shadow-sm">
          <CardContent className="p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <ShowcaseCard
                imageSrc={example.image.src}
                imageAlt={example.image.alt}
                imageWidth={example.image.width}
                imageHeight={example.image.height}
                overlay={example.before}
                label="Base Moondream 3 Preview"
              />
              <ShowcaseCard
                imageSrc={example.image.src}
                imageAlt={example.image.alt}
                imageWidth={example.image.width}
                imageHeight={example.image.height}
                overlay={example.after}
                label="Fine-tuned Moondream 3 Preview"
              />
            </div>
          </CardContent>
        </Card>
      </section>
    </PageContent>
  );
}
